import { apiClient } from '@/shared/api/apiClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useGetUserSession } from './auth'
import { USER_SESSION_QUERY_KEY } from './auth'

export const CURRENT_USER_QUERY_KEY = 'currentUser'

export type CurrentUser = {
  id: string
  firstName: string
  lastName: string
  email: string
  language: string
  organizationId: string
}

export function useCurrentUser() {
  const { data: session } = useGetUserSession()

  return useQuery<CurrentUser | null>({
    queryKey: [CURRENT_USER_QUERY_KEY],
    queryFn: async (): Promise<CurrentUser | null> => {
      try {
        const response = await apiClient.get<CurrentUser>('/v1/user/me')
        return response.data
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 401) {
          return null
        }
        throw error
      }
    },
    enabled: !!session,
    staleTime: Infinity,
    retry: false,
  })
}

export function useUpdateLanguage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (language: string) => {
      await apiClient.put('/v1/user/language', { language })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [USER_SESSION_QUERY_KEY] })
      void queryClient.invalidateQueries({ queryKey: [CURRENT_USER_QUERY_KEY] })
    },
  })
}

export function useUpdateName() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { firstName: string; lastName: string }) => {
      await apiClient.put('/v1/user/name', payload)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CURRENT_USER_QUERY_KEY] })
    },
  })
}

export function useChangePassword() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: {
      currentPassword: string
      newPassword: string
    }) => {
      await apiClient.put('/v1/user/password', payload)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [USER_SESSION_QUERY_KEY] })
      void queryClient.invalidateQueries({ queryKey: [CURRENT_USER_QUERY_KEY] })
    },
  })
}
